import { Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { FormDialog } from '../../../shared/components/FormDialog';
import { DatePickerPtBrField } from '../../../shared/components/DatePickerPtBr';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { employeeFeriasInputSchema, type EmployeeFeriasFormValues } from './schemas';
import type { EmployeeFeriasDto, EmployeeFeriasInput } from './types';

export interface EmployeeFeriasFormDialogProps {
  open: boolean;
  ferias: EmployeeFeriasDto | null;
  hooks: {
    useCreate: () => UseMutationResult<EmployeeFeriasDto, Error, EmployeeFeriasInput>;
    useUpdate: () => UseMutationResult<
      EmployeeFeriasDto,
      Error,
      { id: string; input: EmployeeFeriasInput }
    >;
  };
  onClose: () => void;
}

const DEFAULT_VALUES: EmployeeFeriasFormValues = {
  dataInicio: new Date().toISOString().slice(0, 10),
  dataFim: new Date().toISOString().slice(0, 10),
  observacao: '',
};

export function EmployeeFeriasFormDialog({
  open,
  ferias,
  hooks,
  onClose,
}: EmployeeFeriasFormDialogProps): JSX.Element {
  const { t } = useTranslation();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    EmployeeFeriasFormValues,
    EmployeeFeriasDto,
    EmployeeFeriasInput,
    string
  >({
    open,
    entity: ferias,
    schema: employeeFeriasInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (f) => ({
      dataInicio: f.dataInicio,
      dataFim: f.dataFim,
      observacao: f.observacao ?? '',
    }),
    toInput: (v) => ({
      dataInicio: v.dataInicio,
      dataFim: v.dataFim,
      observacao: v.observacao,
    }),
    getId: (f) => f.dataInicio,
    hooks: { useCreate: hooks.useCreate, useUpdate: hooks.useUpdate },
    messages: {
      created: t('features.colaboradores.ferias.toasts.created'),
      updated: t('features.colaboradores.ferias.toasts.updated'),
    },
    focusField: undefined,
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.colaboradores.ferias.dialog.editTitle')
          : t('features.colaboradores.ferias.dialog.newTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <DatePickerPtBrField
            control={form.control}
            name="dataInicio"
            label={t('features.colaboradores.ferias.fields.dataInicio')}
            disabled={isEdit}
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: isEdit
                  ? t('features.colaboradores.ferias.fields.dataInicioLockedHelper')
                  : t('features.colaboradores.ferias.fields.dataInicioHelper'),
              },
            }}
          />
          <DatePickerPtBrField
            control={form.control}
            name="dataFim"
            label={t('features.colaboradores.ferias.fields.dataFim')}
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: t('features.colaboradores.ferias.fields.dataFimHelper'),
              },
            }}
          />
        </Stack>

        <TextField
          label={t('features.colaboradores.ferias.fields.observacao')}
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          inputProps={{ maxLength: 500 }}
          error={Boolean(form.formState.errors.observacao)}
          helperText={
            form.formState.errors.observacao?.message ??
            t('features.colaboradores.ferias.fields.observacaoHelper')
          }
          {...form.register('observacao')}
        />
      </Stack>
    </FormDialog>
  );
}
